import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import ExpertAvatar from "@components/ExpertAvatar";
import Nbsp from "@components/Nbsp";
import styles from "@components/TimeSelector.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useMyExpert from "@hooks/useMyExpert";
import { GetJourneyWithExpertAndConsultsQuery, GetJourneyWithPlanQuery } from "@typing/Generated";
import { convertTZ, timezoneAbbreviation } from "@utils/timeUtils";

type Expert = {
  firstName: string;
  fullName: string;
  photoUrl: string | null;
};

type Props<T> = {
  clientRoles:
    | GetJourneyWithPlanQuery["journey"]["clientRoles"]
    | GetJourneyWithExpertAndConsultsQuery["journey"]["clientRoles"]
    | null;
  expert?: Expert;
  large?: boolean;
  onSelect: (v: T) => void;
  selected: boolean;
  text: string;
  value: T;
};

const TimeSelector = <T extends unknown>({ clientRoles, expert, large, onSelect, selected, text, value }: Props<T>) => {
  const myExpert = useMyExpert();

  const differentTimezoneRoles = useMemo(
    () => clientRoles?.filter(role => role.client.timezone !== myExpert?.timezone),
    [clientRoles, myExpert?.timezone]
  );

  return (
    <div
      className={`${styles.container} ${selected ? styles.selected : ""} ${large ? styles.large : ""}`}
      data-test="time-selector"
      onClick={() => onSelect(value)}
    >
      <div>
        {large ? (
          <Heading2 color={selected ? "white" : "primary"}>{text}</Heading2>
        ) : (
          <BodyCopy color={selected ? "white" : "primary"}>{text}</BodyCopy>
        )}
        {expert && (
          <div className={styles.expert}>
            <ExpertAvatar expert={expert} size={30} />
            <BodyCopy color={selected ? "white" : "primary"}>{expert.firstName}</BodyCopy>
          </div>
        )}
        {myExpert &&
          clientRoles &&
          differentTimezoneRoles?.map(role => {
            if (role.client.timezone) {
              return (
                <div className={styles.timezone} key={role.id}>
                  <BodyCopy color={selected ? "white" : "primary"}>
                    {convertTZ(value as Date, role.client.timezone)}
                    <Nbsp />
                    {timezoneAbbreviation(role.client.timezone)}
                    <FormattedMessage id="dictionary.punctuation.dash" />
                    {role.client.firstName}
                  </BodyCopy>
                </div>
              );
            }
            return null;
          })}
      </div>
    </div>
  );
};

export default TimeSelector;
